import React, { useState, useEffect } from 'react';
import { useAiConfig, useUpdateAiConfig, useAiTelemetry, useRunAiMaintenance, useAiStatus, useRunAiSweepTick, useRunSalesSync } from '../hooks/useAi';
import { timeAgo, parseUtcDate, formatDateTimeEastern } from '../utils/dates';
import { reasonLabel } from '../utils/aiFeedback';

// ---- formatting for the feedback / outcome blocks (every field may be absent) ----
const num = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
const pctOf = (part, whole) => (num(part) != null && num(whole) > 0 ? `${Math.round((num(part) / num(whole)) * 100)}%` : '—');
const signedPct = (v) => {
  const n = num(v);
  if (n == null) return '—';
  return `${n > 0 ? '+' : ''}${Math.round(n * 10) / 10}%`;
};
const days = (v) => (num(v) == null ? '—' : `${Math.round(num(v) * 10) / 10}d`);
// matchRate7d: a 0–1 fraction (values above 1 are taken as already-percent)
const rate = (v) => {
  const n = num(v);
  if (n == null) return '—';
  return `${Math.round(n <= 1 ? n * 100 : n)}%`;
};
const ago = (v) => timeAgo(parseUtcDate(v)) || '—';
// byVerdict may arrive as { right, wrong } or [{ verdict, n }]
const verdictCounts = (bv) => {
  if (Array.isArray(bv)) return Object.fromEntries(bv.map(r => [r.verdict, r.n]));
  return bv && typeof bv === 'object' ? bv : {};
};
// Watermarks may be a date ("2026-10-05") or an instant; anything else is not shown
const watermarkText = (w) => {
  if (!w || (typeof w !== 'string' && typeof w !== 'number')) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(w)) ? String(w) : formatDateTimeEastern(w);
};
// Sales-sync errors arrive as { at, kind, message } (or a bare string from an
// older server) — never hand an object to JSX
const errorText = (e) => {
  if (e == null || e === '') return '';
  if (typeof e === 'string') return e;
  if (typeof e === 'object') return e.message ? String(e.message) : JSON.stringify(e);
  return String(e);
};
// When / what kind: the server sends lastErrorAt + lastErrorKind beside a
// string lastError; an object lastError may carry its own at / kind. Never
// read .at off a string (that's String.prototype.at).
const errObj = (ss) => (ss?.lastError && typeof ss.lastError === 'object' ? ss.lastError : null);
const lastErrorWhen = (ss) => {
  const at = ss?.lastErrorAt || errObj(ss)?.at;
  return typeof at === 'string' && at ? ` (${ago(at)})` : '';
};
const lastErrorKind = (ss) => {
  const kind = ss?.lastErrorKind || errObj(ss)?.kind;
  return typeof kind === 'string' && kind && kind !== 'other' ? kind : '';
};
// A count only when it really is one
const countOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
// Admin "Run now" calls outlive nginx's 60 s proxy timeout: a 504 or a client
// timeout means the tick is still running server-side, not that it failed
const runNowError = (err, fallback) => {
  if (err?.response?.status === 504 || err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
    return 'Still running on the server — refresh in a minute';
  }
  return err?.response?.data?.error || fallback;
};
// Row order: suggestion statuses, then confidence levels (the two tables' keys never overlap)
const OUTCOME_STATUS_ORDER = ['accepted', 'modified', 'dismissed', 'pending', 'stale', 'superseded', 'high', 'medium', 'low'];

// One outcomes table (rows = suggestion status or confidence)
function OutcomeTable({ title, groups }) {
  const keys = Object.keys(groups || {});
  if (!keys.length) return null;
  keys.sort((a, b) => {
    const ia = OUTCOME_STATUS_ORDER.indexOf(a), ib = OUTCOME_STATUS_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  return (
    <div className="table-container ai-outcome-table">
      <table>
        <thead>
          <tr>
            <th>{title}</th><th>n</th><th>Sold</th><th>Delisted</th><th>Still listed</th><th>Passed / unknown</th>
            <th>Median days to sale</th><th>Sale B vs suggested</th><th>Sale B vs applied</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => {
            const g = groups[k] || {};
            return (
              <tr key={k}>
                <td>{k}</td>
                <td>{g.n ?? 0}</td>
                <td>{pctOf(g.sold, g.n)}</td>
                <td>{pctOf(g.delisted, g.n)}</td>
                <td>{pctOf(g.stillListed, g.n)}</td>
                <td>{(g.eventPassed ?? 0)} / {(g.unknown ?? 0)}</td>
                <td>{days(g.medianDaysToSale)}</td>
                <td>{signedPct(g.medianSaleVsSuggestedPct)}</td>
                <td>{signedPct(g.medianSaleVsAppliedPct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SWEEP_LABELS = {
  idle: 'Idle — nothing due',
  working: 'Working',
  disabled: 'Off — clicks only',
  paused_key: 'Paused — no API key',
  paused_budget: 'Paused — daily budget reached',
  paused_hourly_cap: 'Paused — hourly cap reached',
  paused_auth: 'Paused — Skybox session token dead',
  stopped: 'Stopped',
};

// Admin → AI tab: master switch, model/effort/budget, pilot scope, the
// editable pricing playbook, usage + acceptance telemetry, maintenance.
function AiAdminTab({ onMessage }) {
  const { data, isLoading, error } = useAiConfig();
  const { data: telemetry } = useAiTelemetry();
  const { data: status } = useAiStatus({ refetchInterval: 30000 });
  const updateConfig = useUpdateAiConfig();
  const runMaintenance = useRunAiMaintenance();
  const runSweep = useRunAiSweepTick();
  const runSalesSync = useRunSalesSync();

  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (data?.config && !draft) setDraft(data.config);
  }, [data, draft]);

  if (isLoading || !draft) return <div className="loading">Loading AI settings…</div>;
  if (error) return <div className="message message-error">Failed to load AI settings</div>;

  const cfg = data.config;
  const health = data.health || {};
  const spend = data.spend || { cost: 0, runs: 0 };
  const ledger = status?.ledger || data.ledger || null;
  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg);
  // New telemetry blocks — null/absent until the backend has data for them
  const salesSync = status?.salesSync || telemetry?.salesSync || null;
  const feedback = telemetry?.feedback || null;
  const outcomes = telemetry?.outcomes || null;
  const verdicts = verdictCounts(feedback?.byVerdict);

  const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));

  const save = () => {
    const changes = {};
    for (const k of Object.keys(draft)) if (draft[k] !== cfg[k]) changes[k] = draft[k];
    if (!Object.keys(changes).length) return;
    updateConfig.mutate(changes, {
      onSuccess: (res) => { setDraft(res.data.config); onMessage?.('AI settings saved'); },
      onError: (err) => onMessage?.(err.response?.data?.error || 'Failed to save', true),
    });
  };

  const toggleEnabled = () => {
    const next = !cfg.enabled;
    updateConfig.mutate({ enabled: next }, {
      onSuccess: (res) => { setDraft(res.data.config); onMessage?.(next ? 'AI assistant enabled' : 'AI assistant disabled'); },
    });
  };

  const pilotText = (() => {
    try { const a = JSON.parse(draft.pilot_performers || '[]'); return Array.isArray(a) ? a.join(', ') : ''; } catch { return draft.pilot_performers || ''; }
  })();
  const setPilotText = (txt) => {
    const arr = txt.split(',').map(s => s.trim()).filter(Boolean);
    set('pilot_performers', arr.length ? JSON.stringify(arr) : '');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>AI Pricing Assistant</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className={`ai-status-pill ${cfg.enabled ? 'on' : 'off'}`}>{cfg.enabled ? 'ON' : 'OFF'}</span>
          <button className={cfg.enabled ? 'btn-secondary' : 'btn-primary'} onClick={toggleEnabled} disabled={updateConfig.isPending}>
            {cfg.enabled ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </div>

      {!health.keyPresent && (
        <div className="message message-error" style={{ marginBottom: 12 }}>
          ANTHROPIC_API_KEY is not set on the server. Market history and flags still run, but no suggestions or Q&A until a key is added to .env and the app restarts.
        </div>
      )}

      {ledger?.ledgerNote && (
        <div className="message message-error" style={{ marginBottom: 12 }}>{ledger.ledgerNote}</div>
      )}

      <div className="ai-admin-grid">
        <div className="ai-admin-card">
          <h4>Today</h4>
          <div className="big">${spend.cost.toFixed(2)}</div>
          <div className="ai-admin-note">
            {ledger?.runs?.complete ?? spend.runs} analyses{ledger?.avgCostPerCompleteRunUsd != null ? ` · avg $${ledger.avgCostPerCompleteRunUsd.toFixed(2)} each` : ''}
            {ledger?.runs?.error ? ` · ${ledger.runs.error} errored ($${(ledger.errorRunsRecordedUsd || 0).toFixed(2)})` : ''}
            {ledger?.runs?.orphaned ? ` · ${ledger.runs.orphaned} orphaned` : ''}
            {` · budget $${cfg.daily_budget_usd}/day`}
          </div>
        </div>
        <div className="ai-admin-card"><h4>This month</h4><div className="big">${(telemetry?.thisMonth?.costUsd ?? 0).toFixed(2)}</div><div className="ai-admin-note">{telemetry?.thisMonth?.runs ?? 0} runs</div></div>
        <div className="ai-admin-card"><h4>Suggestions</h4><div className="big">{telemetry?.byStatus?.pending ?? 0}</div><div className="ai-admin-note">pending · {telemetry?.byStatus?.accepted ?? 0} accepted · {telemetry?.byStatus?.modified ?? 0} modified · {telemetry?.byStatus?.dismissed ?? 0} dismissed</div></div>
        <div className="ai-admin-card">
          <h4>Market memory</h4>
          <div className="big">{health.snapshotEvents ?? 0}</div>
          <div className="ai-admin-note">events with history · {health.snapshots ?? 0} snapshots · {health.salesHistory ?? 0} past sales</div>
          <div className="ai-admin-note ai-sales-sync-line">
            Sales sync:{' '}
            {salesSync
              ? <>
                  {salesSync.lastRunAt ? `last ${ago(salesSync.lastRunAt)}` : 'not run yet'}
                  {countOrNull(salesSync.rowsIngestedToday) != null ? ` · ${countOrNull(salesSync.rowsIngestedToday)} rows today` : ''}
                  {watermarkText(salesSync.watermark) ? ` · through ${watermarkText(salesSync.watermark)}` : ''}
                  {errorText(salesSync.lastError)
                    ? <><br />last error{lastErrorKind(salesSync) ? ` (${lastErrorKind(salesSync)})` : ''}: {errorText(salesSync.lastError)}{lastErrorWhen(salesSync)}</>
                    : null}
                </>
              : 'no status yet'}
          </div>
          <button
            className="btn-secondary btn-sm"
            style={{ marginTop: 6 }}
            onClick={() => runSalesSync.mutate(undefined, {
              onSuccess: () => onMessage?.('Sales sync tick complete'),
              onError: (err) => {
                const msg = runNowError(err, 'Sales sync failed');
                onMessage?.(msg, !msg.startsWith('Still running'));
              },
            })}
            disabled={runSalesSync.isPending}
            title="Pull new invoices from Skybox now and match sales to listings (one tick)"
          >
            {runSalesSync.isPending ? 'Syncing…' : 'Run sales sync now'}
          </button>
        </div>
        <div className="ai-admin-card">
          <h4>Background analysis</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{SWEEP_LABELS[status?.scheduler?.status] || status?.scheduler?.status || '—'}</div>
          <div className="ai-admin-note">
            {status?.scheduler?.processed ?? 0} analyzed since boot · {status?.scheduler?.queued ?? 0} queued
            {status?.scheduler ? ` · ${status.scheduler.runsThisHour ?? 0}/${status.scheduler.sweepMaxPerHour ?? '?'} this hour` : ''}
            {status?.scheduler?.lastRunAt ? ` · last ${timeAgo(status.scheduler.lastRunAt)}` : ''}
            {status?.scheduler?.lastError ? <><br />last error: {status.scheduler.lastError}</> : null}
          </div>
          <button className="btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => runSweep.mutate(undefined, { onSuccess: () => onMessage?.('Sweep tick complete') })} disabled={runSweep.isPending}>
            {runSweep.isPending ? 'Running…' : 'Run one tick now'}
          </button>
        </div>
      </div>

      <h3 style={{ marginTop: 8 }}>When does it spend money?</h3>
      <div className="ai-admin-note" style={{ marginBottom: 6 }}>
        By default only when someone clicks <b>Analyze now</b> or <b>Ask</b> — roughly $0.10–0.40 per analysis at the current model/effort. The two switches below add background spend.
      </div>
      <div className="ai-admin-row">
        <label>Analyze automatically when a pricer opens Compare</label>
        <input type="checkbox" checked={!!draft.analyze_on_compare} onChange={e => set('analyze_on_compare', e.target.checked)} />
        <span className="ai-admin-note">Queues a background analysis on each Compare open (skipped if analyzed within the re-analyze window).</span>
      </div>
      <div className="ai-admin-row">
        <label>Background sweep</label>
        <input type="checkbox" checked={!!draft.sweep_enabled} onChange={e => set('sweep_enabled', e.target.checked)} />
        <span className="ai-admin-note">Proactively re-analyze the most urgent events on a schedule (paced to protect Skybox).</span>
      </div>
      <div className="ai-admin-row">
        <label>Background cap (analyses / hour)</label>
        <input type="number" min="0" step="1" value={draft.sweep_max_per_hour} onChange={e => set('sweep_max_per_hour', Number(e.target.value))} style={{ width: 90 }} />
        <span className="ai-admin-note">Applies to both switches above. {status?.scheduler ? `${status.scheduler.runsThisHour ?? 0} used this hour.` : ''}</span>
      </div>
      <div className="ai-admin-row">
        <label>Don't re-analyze in background within (minutes)</label>
        <input type="number" min="0" step="5" value={draft.min_reanalyze_minutes} onChange={e => set('min_reanalyze_minutes', Number(e.target.value))} style={{ width: 90 }} />
        <span className="ai-admin-note">Clicks always run (unless nothing changed).</span>
      </div>
      <div className="ai-admin-row">
        <label>Daily budget cap (USD)</label>
        <input type="number" min="1" step="1" value={draft.daily_budget_usd} onChange={e => set('daily_budget_usd', Number(e.target.value))} style={{ width: 100 }} />
        <span className="ai-admin-note">Runaway guard — all analysis pauses until midnight when reached.</span>
      </div>

      <h3 style={{ marginTop: 16 }}>Model &amp; quality</h3>
      <div className="ai-admin-row">
        <label>Model</label>
        <select value={draft.model} onChange={e => set('model', e.target.value)}>
          <option value="claude-fable-5">claude-fable-5 (most capable, ~5× Sonnet cost)</option>
          <option value="claude-opus-5">claude-opus-5 (~2.5× Sonnet cost)</option>
          <option value="claude-sonnet-5">claude-sonnet-5 (cheapest; no refusal fallback)</option>
        </select>
      </div>
      <div className="ai-admin-row">
        <label>Effort — clicks (Analyze now, Ask)</label>
        <select value={draft.effort_interactive} onChange={e => set('effort_interactive', e.target.value)}>
          {['low', 'medium', 'high', 'xhigh', 'max'].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <span className="ai-admin-note">Higher = more thinking tokens = more cost. high is the sweet spot.</span>
      </div>
      <div className="ai-admin-row">
        <label>Effort — background</label>
        <select value={draft.effort_sweep} onChange={e => set('effort_sweep', e.target.value)}>
          {['low', 'medium', 'high', 'xhigh', 'max'].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <div className="ai-admin-row">
        <label>Second-opinion verification pass</label>
        <select value={draft.verify_mode} onChange={e => set('verify_mode', e.target.value)}>
          <option value="manual">clicks only</option>
          <option value="always">always</option>
          <option value="off">off</option>
        </select>
        <select value={draft.effort_verify} onChange={e => set('effort_verify', e.target.value)} title="verification effort">
          {['low', 'medium', 'high'].map(v => <option key={v} value={v}>{v} effort</option>)}
        </select>
        <span className="ai-admin-note">Roughly doubles the cost of an analysis; catches plausible-but-wrong suggestions.</span>
      </div>
      <div className="ai-admin-row">
        <label>Analysis output cap (tokens)</label>
        <input type="number" min="4096" step="1024" value={draft.analysis_max_tokens} onChange={e => set('analysis_max_tokens', Number(e.target.value))} style={{ width: 100 }} />
        <span className="ai-admin-note">Thinking counts against this. Too low → truncated (and still billed). 16000 is safe.</span>
      </div>
      <div className="ai-admin-row">
        <label>Pilot scope (performers, comma-separated)</label>
        <input type="text" value={pilotText} onChange={e => setPilotText(e.target.value)} placeholder="blank = all performers" style={{ minWidth: 340 }} />
      </div>

      <h3 style={{ marginTop: 16 }}>Pricing playbook</h3>
      <div className="ai-admin-note" style={{ marginBottom: 6 }}>
        Your house rules in plain English. The analyst follows these over its general reasoning. Edit freely — changes apply to the next analysis.
      </div>
      <textarea className="ai-playbook" value={draft.playbook} onChange={e => set('playbook', e.target.value)}
        placeholder={'e.g.\n- Inside 7 days, undercut the section floor by 2–5% unless notes say hold.\n- Never price Tier S below cost before day-of.\n- Parking: price to move; match floor immediately.\n- Broadway: hold premium orchestra until 14 days out.'} />
      {cfg.playbook_draft && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: '#8fd3ff' }}>
            AI-drafted playbook available (from your sales history + decisions) — click to review
          </summary>
          <pre className="ai-playbook" style={{ whiteSpace: 'pre-wrap', marginTop: 8, minHeight: 0 }}>{cfg.playbook_draft}</pre>
          <button className="btn-secondary btn-sm" onClick={() => set('playbook', cfg.playbook_draft)}>Load draft into playbook (then edit & save)</button>
        </details>
      )}

      <div className="ai-admin-row" style={{ marginTop: 10 }}>
        <button className="btn-primary" onClick={save} disabled={!dirty || updateConfig.isPending}>{updateConfig.isPending ? 'Saving…' : 'Save settings'}</button>
        {dirty && <button className="btn-secondary" onClick={() => setDraft(cfg)}>Discard</button>}
        <span style={{ flex: 1 }} />
        <button className="btn-secondary btn-sm" onClick={() => runMaintenance.mutate(undefined, { onSuccess: () => onMessage?.('Maintenance complete') })} disabled={runMaintenance.isPending} title="Compact history, prune, rebuild sales stats, back up the database">
          {runMaintenance.isPending ? 'Running…' : 'Run maintenance now'}
        </button>
      </div>

      {telemetry?.byPricer?.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Acceptance by pricer</h3>
          <div className="table-container">
            <table>
              <thead><tr><th>Pricer</th><th>Accepted</th><th>Modified</th><th>Dismissed</th><th>Acceptance</th></tr></thead>
              <tbody>
                {telemetry.byPricer.map(p => (
                  <tr key={p.userId}>
                    <td>{p.displayName}</td><td>{p.accepted}</td><td>{p.modified}</td><td>{p.dismissed}</td>
                    <td>{p.acceptanceRate != null ? `${p.acceptanceRate}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {telemetry.modifiedCount > 0 && (
            <div className="ai-admin-note" style={{ marginTop: 6 }}>
              When pricers modify a suggestion they move it by {telemetry.modifiedAvgAbsDeltaPct}% on average ({telemetry.modifiedCount} modifications).
            </div>
          )}
          {telemetry.dismissReasons?.length > 0 && (
            <div className="ai-admin-note" style={{ marginTop: 4 }}>
              Dismiss reasons: {telemetry.dismissReasons.map(r => `${reasonLabel(r.dismiss_reason)} (${r.n})`).join(' · ')}
            </div>
          )}
        </>
      )}

      <h3 style={{ marginTop: 20 }}>Feedback</h3>
      {!feedback || !(num(feedback.total) > 0) ? (
        <div className="ai-admin-note">No feedback data yet — ★ Rate on Compare (or a dismiss with a reason) records it.</div>
      ) : (
        <>
          <div className="ai-admin-note">
            <b>{feedback.labels ?? 0}</b> labels (admin ratings — these train the model) · {feedback.total} ratings in all
            · right {verdicts.right ?? 0} · wrong {verdicts.wrong ?? 0}
          </div>
          {num(feedback.preferredVsSuggested?.n) > 0 && (
            <div className="ai-admin-note" style={{ marginTop: 4 }}>
              Your price vs the AI's: median {signedPct(feedback.preferredVsSuggested.medianPct)}
              {' '}(middle half {signedPct(feedback.preferredVsSuggested.p25Pct)} to {signedPct(feedback.preferredVsSuggested.p75Pct)})
              {' '}over {feedback.preferredVsSuggested.n} rating{feedback.preferredVsSuggested.n === 1 ? '' : 's'} with an "I'd use" price
            </div>
          )}
          <div className="ai-feedback-tables">
            {feedback.byReason?.length > 0 && (
              <div className="table-container">
                <table>
                  <thead><tr><th>Reason</th><th>Ratings</th><th>Labels</th></tr></thead>
                  <tbody>
                    {feedback.byReason.map(r => (
                      <tr key={r.reasonCode || 'none'}>
                        <td>{reasonLabel(r.reasonCode) || '—'}</td><td>{r.n ?? 0}</td><td>{r.labels ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {feedback.byConfidence?.length > 0 && (
              <div className="table-container">
                <table>
                  <thead><tr><th>AI confidence</th><th>Right</th><th>Wrong</th></tr></thead>
                  <tbody>
                    {feedback.byConfidence.map(c => (
                      <tr key={c.confidence || 'none'}>
                        <td>{c.confidence || '—'}</td><td>{c.right ?? 0}</td><td>{c.wrong ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {feedback.byRater?.length > 0 && (
              <div className="table-container">
                <table>
                  <thead><tr><th>Rater</th><th>Role</th><th>Counts as label</th><th>Ratings</th><th>Wrong</th></tr></thead>
                  <tbody>
                    {feedback.byRater.map(r => (
                      <tr key={r.userId}>
                        <td>{r.displayName || `user ${r.userId}`}</td><td>{r.role || '—'}</td>
                        <td>{r.isLabel ? 'yes' : 'no'}</td><td>{r.n ?? 0}</td><td>{r.wrong ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <h3 style={{ marginTop: 20 }}>What happened next</h3>
      {!outcomes || !(num(outcomes.n) > 0) ? (
        <div className="ai-admin-note">
          No outcome data yet — each suggestion is scored nightly (sold / delisted / still listed) once listings and sales are being tracked.
          {outcomes?.lifecycle ? ` Listings tracked so far: ${outcomes.lifecycle.tracked ?? 0}.` : ''}
        </div>
      ) : (
        <>
          <div className="ai-admin-note">
            {outcomes.n} suggestions scored{outcomes.computedAt ? ` ${ago(outcomes.computedAt)}` : ''}. Sale B is the listing's B at the moment it sold; negative = sold below the price.
          </div>
          <OutcomeTable title="Suggestion status" groups={outcomes.byStatus} />
          <OutcomeTable title="AI confidence" groups={outcomes.byConfidence} />
          {outcomes.labelsVsOutcome && (
            <div className="ai-admin-note" style={{ marginTop: 6 }}>
              {outcomes.labelsVsOutcome.adminWrong && (
                <div>
                  You said wrong: {outcomes.labelsVsOutcome.adminWrong.n ?? 0}
                  {' '}· {pctOf(outcomes.labelsVsOutcome.adminWrong.sold, outcomes.labelsVsOutcome.adminWrong.n)} sold
                  {' '}· sale B vs AI {signedPct(outcomes.labelsVsOutcome.adminWrong.medianSaleVsSuggestedPct)}
                  {' '}· vs your price {signedPct(outcomes.labelsVsOutcome.adminWrong.medianSaleVsPreferredPct)}
                </div>
              )}
              {outcomes.labelsVsOutcome.adminRight && (
                <div>
                  You said right: {outcomes.labelsVsOutcome.adminRight.n ?? 0}
                  {' '}· {pctOf(outcomes.labelsVsOutcome.adminRight.sold, outcomes.labelsVsOutcome.adminRight.n)} sold
                  {' '}· sale B vs AI {signedPct(outcomes.labelsVsOutcome.adminRight.medianSaleVsSuggestedPct)}
                </div>
              )}
            </div>
          )}
          {outcomes.lifecycle && (
            <div className="ai-admin-note" style={{ marginTop: 6 }}>
              Listings tracked {outcomes.lifecycle.tracked ?? 0} · removed last 7 d {outcomes.lifecycle.removedLast7d ?? 0}
              {' '}· matched to a sale {outcomes.lifecycle.matchedLast7d ?? 0} ({rate(outcomes.lifecycle.matchRate7d)})
              {' '}· unknown {outcomes.lifecycle.unknownLast7d ?? 0}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AiAdminTab;
